/* eslint-disable @ms/no-unnecessary-spread */
import type { StoreApi, UseBoundStore } from 'zustand';
import { create } from 'zustand';

interface SearchSession {
  readonly conversationId: string;
  readonly conversationStart: number;
  readonly logicalId: string;
}
interface SearchSessionState {
  readonly globalSearchSession: SearchSession;
  readonly typeaheadSearchSession: SearchSession;

  readonly setSessionInformations: (sessionType: SessionType, sessionInformations: SearchSession) => void;
  readonly setLogicalId: (sessionType: SessionType, logicalId: string) => void;
  readonly resetSession: (sessionType: SessionType) => void;
}

export type SessionType = 'globalSearchSession' | 'typeaheadSearchSession';

const defaultSearchSession: SearchSession = {
  conversationId: 'ERROR - no-conversation-id',
  conversationStart: 0,
  logicalId: 'ERROR - no-logical-id',
};

export const useSearchSessionState: UseBoundStore<StoreApi<SearchSessionState>> = create<SearchSessionState>()(
  (set) => ({
    globalSearchSession: { ...defaultSearchSession },
    typeaheadSearchSession: { ...defaultSearchSession },

    setSessionInformations: (sessionType: SessionType, sessionInformations: SearchSession) =>
      set(() => ({ [sessionType]: sessionInformations })),
    setLogicalId: (sessionType: SessionType, logicalId: string) =>
      set((state) => ({ [sessionType]: { ...state[sessionType], logicalId } })),
    resetSession: (sessionType: SessionType) => set(() => ({ [sessionType]: { ...defaultSearchSession } })),
  })
);

export const useSetSessionInformations = (): ((sessionType: SessionType, sessionInformations: SearchSession) => void) =>
  useSearchSessionState((state) => state.setSessionInformations);
export const useSetLogicalId = (): ((sessionType: SessionType, logicalId: string) => void) =>
  useSearchSessionState((state) => state.setLogicalId);
export const useResetSession = (): ((sessionType: SessionType) => void) =>
  useSearchSessionState((state) => state.resetSession);
export const useSearchSession = (sessionType?: SessionType): SearchSession =>
  useSearchSessionState((state) => {
    if (sessionType === 'typeaheadSearchSession') {
      return state.typeaheadSearchSession;
    }

    return state.globalSearchSession;
  });
